<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  version: {
    type: String,
    required: true,
  },
  config: {
    type: Object,
    required: true,
  },
  inboxes: {
    type: Array,
    default: () => [],
  },
  isCreating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['create']);

const { t } = useI18n();

// Get version display name
const versionName = computed(() => {
  const nameMap = {
    1.4: `${t('LIA_AGENTS.VERSIONS.LIA_14')} - ${t('LIA_AGENTS.VERSIONS.LIA_14_SUBTITLE')}`,
    2.1: `${t('LIA_AGENTS.VERSIONS.LIA_21')} - ${t('LIA_AGENTS.VERSIONS.LIA_21_SUBTITLE')}`,
    3.1: `${t('LIA_AGENTS.VERSIONS.LIA_31')} - ${t('LIA_AGENTS.VERSIONS.LIA_31_SUBTITLE')}`,
  };
  return nameMap[props.version] || `LIA ${props.version}`;
});

// Get version color
const versionColor = computed(() => {
  const colors = {
    1.4: 'blue',
    2.1: 'purple',
    3.1: 'green',
  };
  return colors[props.version] || 'blue';
});

// Get selected inbox names
const selectedInboxNames = computed(() => {
  return props.inboxes
    .filter(i => props.config.inbox_ids?.includes(i.id))
    .map(i => i.name);
});

// ERP provider name
const erpProviderName = computed(() => {
  const providers = {
    ixc: t('LIA_AGENTS.ERP_PROVIDERS.ixc'),
    mycore: t('LIA_AGENTS.ERP_PROVIDERS.mycore'),
    sgp: t('LIA_AGENTS.ERP_PROVIDERS.sgp'),
    voalle: t('LIA_AGENTS.ERP_PROVIDERS.voalle'),
    hubsoft: t('LIA_AGENTS.ERP_PROVIDERS.hubsoft'),
  };
  return providers[props.config.erp_type] || props.config.erp_type;
});

const handleCreate = () => {
  emit('create');
};
</script>

<template>
  <div class="space-y-6 max-w-2xl mx-auto">
    <div class="text-center">
      <div
        class="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center"
      >
        <span class="i-lucide-check-circle size-8 text-green-600" />
      </div>
      <h2 class="text-2xl font-bold text-n-slate-12">
        {{ t('LIA_AGENTS.WIZARD.TITLE_REVIEW') }}
      </h2>
      <p class="mt-2 text-n-slate-11">
        {{ t('LIA_AGENTS.WIZARD.SUBTITLE_REVIEW') }}
      </p>
    </div>

    <!-- Resumo -->
    <div class="bg-n-solid-3 rounded-xl p-6 space-y-4">
      <!-- Versao -->
      <div
        class="flex items-center justify-between py-3 border-b border-n-weak"
      >
        <span class="text-n-slate-11">{{
          t('LIA_AGENTS.WIZARD.REVIEW_VERSION')
        }}</span>
        <span
          class="px-3 py-1 rounded-full text-sm font-medium"
          :class="{
            'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300':
              versionColor === 'blue',
            'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300':
              versionColor === 'purple',
            'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300':
              versionColor === 'green',
          }"
        >
          {{ versionName }}
        </span>
      </div>

      <!-- Nome do Agente -->
      <div
        class="flex items-center justify-between py-3 border-b border-n-weak"
      >
        <span class="text-n-slate-11">{{
          t('LIA_AGENTS.WIZARD.REVIEW_AGENT_NAME')
        }}</span>
        <span class="font-medium text-n-slate-12">{{ config.name }}</span>
      </div>

      <!-- Empresa -->
      <div
        class="flex items-center justify-between py-3 border-b border-n-weak"
      >
        <span class="text-n-slate-11">{{
          t('LIA_AGENTS.WIZARD.REVIEW_COMPANY')
        }}</span>
        <span class="font-medium text-n-slate-12">{{
          config.company_name
        }}</span>
      </div>

      <!-- Canais -->
      <div class="flex items-start justify-between py-3 border-b border-n-weak">
        <span class="text-n-slate-11">{{
          t('LIA_AGENTS.WIZARD.REVIEW_CHANNELS')
        }}</span>
        <div class="flex flex-wrap gap-2 justify-end">
          <span
            v-for="inboxName in selectedInboxNames"
            :key="inboxName"
            class="px-2 py-1 bg-n-alpha-3 rounded text-sm text-n-slate-12"
          >
            {{ inboxName }}
          </span>
        </div>
      </div>

      <!-- ERP (se LIA 3.1) -->
      <template v-if="version === '3.1'">
        <div
          class="flex items-center justify-between py-3 border-b border-n-weak"
        >
          <span class="text-n-slate-11">{{
            t('LIA_AGENTS.WIZARD.REVIEW_ERP')
          }}</span>
          <span class="font-medium text-n-slate-12">{{ erpProviderName }}</span>
        </div>

        <div class="flex items-center justify-between py-3">
          <span class="text-n-slate-11">{{
            t('LIA_AGENTS.WIZARD.REVIEW_API_URL')
          }}</span>
          <span class="font-medium text-n-slate-12 text-sm truncate max-w-xs">
            {{ config.erp_url }}
          </span>
        </div>
      </template>
    </div>

    <!-- Aviso -->
    <div class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
      <div class="flex items-start gap-3">
        <span class="i-lucide-info size-5 text-blue-600 mt-0.5" />
        <div class="text-sm text-blue-700 dark:text-blue-300">
          <p class="font-medium mb-1">
            {{ t('LIA_AGENTS.WIZARD.ACTIVATION_INFO') }}
          </p>
          <ul class="list-disc list-inside space-y-1">
            <li>{{ t('LIA_AGENTS.WIZARD.ACTIVATION_ITEM_1') }}</li>
            <li>{{ t('LIA_AGENTS.WIZARD.ACTIVATION_ITEM_2') }}</li>
            <li>{{ t('LIA_AGENTS.WIZARD.ACTIVATION_ITEM_3') }}</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Botao Criar -->
    <button
      type="button"
      class="w-full py-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
      :disabled="isCreating"
      @click="handleCreate"
    >
      <span v-if="isCreating" class="i-lucide-loader-2 size-5 animate-spin" />
      <span v-else class="i-lucide-rocket size-5" />
      {{
        isCreating
          ? t('LIA_AGENTS.WIZARD.CREATING_AGENT')
          : t('LIA_AGENTS.WIZARD.CREATE_AND_ACTIVATE')
      }}
    </button>
  </div>
</template>
